import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Spinner } from '@/components/ui/spinner';
import OnboardingPageHeader from '../ComponentOnboarding/OnboardingPageHeader';
import TestScriptSelectionCard from '../ComponentOnboarding/TestScriptSelectionCard';
import { AddRepositoryBackButton, FilterSelect } from './AddRepositoryPickPanel';
import {
  buildReleaseSelectionsFromEnvironments,
  type AddRepositoryPickSelections,
  type ResourceOption,
} from './addRepositoryTypes';
import { getOrganizationDetails } from '@/components/smartai/api/getOrganizationDetails';
import { getProjectsDetails } from '@/components/smartai/api/getProjectsDetails';
import { getRepositoryDetails } from '@/components/smartai/api/getRepositoryDetails';
import { getBranchesDetails } from '@/components/smartai/api/getBranchDetails';
import { getPipelineDetails } from '@/components/smartai/api/getPipelineDetails';
import { getReleaseEnvironments } from '@/components/smartai/api/getReleaseEnvironments';
import {
  detectRepository,
  type DetectRepositoryBody,
} from '@/components/smartai/api/detectRepository';
import {
  detectGithubRepository,
  getGithubBranches,
  getGithubOrganizations,
  getGithubRepositories,
  getGithubTestScripts,
  getGithubWorkflowJobs,
  getGithubWorkflows,
  type GithubDetectBody,
} from '@/components/smartai/api/githubOnboarding';
import {
  getScriptCandidates,
  type ReleasePipelineSelection,
  type TestScriptCandidate,
} from '@/components/smartai/api/getScriptCandidates';
import { fetchJenkinsJobs } from '@/components/smartai/api/jenkinsOnboarding';
import { enableRepositoryCapture } from '@/components/smartai/api/captureApi';
import { filterNonTestFiles } from '@/utils/filterFilePaths';
import { getApiErrorMessage } from '@/utils';
import { useAppDispatch } from '@/state/store';
import {
  setOnboardingBatchInputs,
  setOnboardingEmails,
  setScanOnly,
} from '@/state/slices/onboardingSlice';
import { assertScmProvider } from '@/types/scm-provider';
import type { PipelinePlatform } from '@/types/pipeline-platform';

const ONBOARDED_BY = 'developer@example.com';

const ADO_PIPELINE_PLATFORM_OPTIONS: ResourceOption[] = [
  { value: 'ado', label: 'Azure Pipelines' },
  { value: 'jenkins', label: 'Jenkins' },
];

const GITHUB_PIPELINE_PLATFORM_OPTIONS: ResourceOption[] = [
  { value: 'github-actions', label: 'GitHub Actions' },
  { value: 'jenkins', label: 'Jenkins' },
];

type AddRepositoryTestAutomationPanelProps = {
  pick: AddRepositoryPickSelections;
  onBack: () => void;
  onOnboardComplete: () => void;
};

const AddRepositoryTestAutomationPanel = ({
  pick,
  onBack,
  onOnboardComplete,
}: AddRepositoryTestAutomationPanelProps) => {
  const dispatch = useAppDispatch();
  const isGithub = pick.provider === 'github';

  const [testPat, setTestPat] = useState('');
  const testPatRef = useRef(testPat);
  testPatRef.current = testPat;

  const [testOrgs, setTestOrgs] = useState<ResourceOption[]>([]);
  const [testProjects, setTestProjects] = useState<ResourceOption[]>([]);
  const [testRepos, setTestRepos] = useState<ResourceOption[]>([]);
  const [testBranches, setTestBranches] = useState<ResourceOption[]>([]);
  const [testRepoRemoteUrls, setTestRepoRemoteUrls] = useState<
    Record<string, string>
  >({});
  const [selectedTestOrg, setSelectedTestOrg] = useState('');
  const [selectedTestProject, setSelectedTestProject] = useState('');
  const [selectedTestRepo, setSelectedTestRepo] = useState('');
  const [selectedTestBranch, setSelectedTestBranch] = useState('');

  const [testPipelinePlatform, setTestPipelinePlatform] = useState<
    PipelinePlatform | ''
  >('');
  const [testPipelines, setTestPipelines] = useState<ResourceOption[]>([]);
  const [selectedTestPipeline, setSelectedTestPipeline] = useState('');
  const [testPipelinesLoading, setTestPipelinesLoading] = useState(false);

  const [testWorkflows, setTestWorkflows] = useState<ResourceOption[]>([]);
  const [selectedTestWorkflow, setSelectedTestWorkflow] = useState('');
  const [testWorkflowsLoading, setTestWorkflowsLoading] = useState(false);
  const [testJobs, setTestJobs] = useState<ResourceOption[]>([]);
  const [selectedTestJob, setSelectedTestJob] = useState('');
  const [testJobsLoading, setTestJobsLoading] = useState(false);

  const [jenkinsBaseUrl, setJenkinsBaseUrl] = useState('');
  const [jenkinsUsername, setJenkinsUsername] = useState('');
  const [jenkinsApiToken, setJenkinsApiToken] = useState('');
  const [jenkinsJobs, setJenkinsJobs] = useState<ResourceOption[]>([]);
  const [jenkinsJobUrls, setJenkinsJobUrls] = useState<Record<string, string>>(
    {},
  );
  const [selectedJenkinsJob, setSelectedJenkinsJob] = useState('');
  const [jenkinsJobsLoading, setJenkinsJobsLoading] = useState(false);
  const [jenkinsJobsError, setJenkinsJobsError] = useState<string | null>(null);

  const [releaseSelections, setReleaseSelections] = useState<
    ReleasePipelineSelection[]
  >([]);

  const [testOrgsLoading, setTestOrgsLoading] = useState(false);
  const [testProjectsLoading, setTestProjectsLoading] = useState(false);
  const [testReposLoading, setTestReposLoading] = useState(false);
  const [testBranchesLoading, setTestBranchesLoading] = useState(false);
  const [testFetchError, setTestFetchError] = useState<string | null>(null);

  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [scriptsError, setScriptsError] = useState<string | null>(null);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [scriptCandidates, setScriptCandidates] = useState<
    TestScriptCandidate[]
  >([]);
  const [selectedScriptPaths, setSelectedScriptPaths] = useState<Set<string>>(
    new Set(),
  );

  const [onboardLoading, setOnboardLoading] = useState(false);
  const [onboardError, setOnboardError] = useState<string | null>(null);

  const resetScriptState = useCallback(() => {
    setScriptsError(null);
    setScriptsLoaded(false);
    setScriptCandidates([]);
    setSelectedScriptPaths(new Set());
    setReleaseSelections([]);
  }, []);

  const resetPipelinePlatformState = useCallback(() => {
    setTestPipelinePlatform('');
    setTestPipelines([]);
    setSelectedTestPipeline('');
    setTestPipelinesLoading(false);
    setSelectedTestWorkflow('');
    setTestJobs([]);
    setSelectedTestJob('');
    setTestJobsLoading(false);
    setJenkinsBaseUrl('');
    setJenkinsUsername('');
    setJenkinsApiToken('');
    setJenkinsJobs([]);
    setJenkinsJobUrls({});
    setSelectedJenkinsJob('');
    setJenkinsJobsLoading(false);
    setJenkinsJobsError(null);
    resetScriptState();
  }, [resetScriptState]);

  const resetTestDownstream = useCallback(() => {
    setSelectedTestProject('');
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setTestProjects([]);
    setTestRepos([]);
    setTestBranches([]);
    setTestRepoRemoteUrls({});
    resetPipelinePlatformState();
  }, [resetPipelinePlatformState]);

  useEffect(() => {
    const token = testPat.trim();
    if (token.length < 5) {
      setTestOrgs([]);
      setSelectedTestOrg('');
      setTestOrgsLoading(false);
      setTestFetchError(null);
      return;
    }

    let cancelled = false;
    setTestOrgsLoading(true);
    setTestFetchError(null);
    setTestOrgs([]);
    setSelectedTestOrg('');
    resetTestDownstream();

    const load = async () => {
      try {
        if (isGithub) {
          const res = await getGithubOrganizations('github', token);
          if (cancelled) return;
          if (res.success === false) {
            throw new Error(res.message || 'Failed to fetch test organizations');
          }
          const options = (res.data?.organizations ?? []).map(org => ({
            value: org.login,
            label:
              org.type === 'User'
                ? `${org.login} (you)`
                : org.name?.trim() || org.login,
          }));
          if (options.length === 0) {
            setTestFetchError('No organizations found for this token.');
          }
          setTestOrgs(options);
        } else {
          const res = await getOrganizationDetails('azure-devops', token);
          if (cancelled) return;
          if (res.success === false) {
            throw new Error(res.message || 'Failed to fetch test organizations');
          }
          const options = (res.data?.value ?? []).map(org => ({
            value: org.accountName,
            label: org.accountName,
          }));
          if (options.length === 0) {
            setTestFetchError('No organizations found for this token.');
          }
          setTestOrgs(options);
        }
      } catch (err) {
        if (cancelled) return;
        setTestOrgs([]);
        setTestFetchError(
          getApiErrorMessage(err, 'Failed to fetch test organizations'),
        );
      } finally {
        if (!cancelled) setTestOrgsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [testPat, isGithub, resetTestDownstream]);

  useEffect(() => {
    resetTestDownstream();
    if (!selectedTestOrg) return;

    const token = testPatRef.current.trim();
    if (!token) return;

    let cancelled = false;
    if (isGithub) {
      setTestReposLoading(true);
      setTestRepos([]);
      void getGithubRepositories('github', token, selectedTestOrg)
        .then(res => {
          if (cancelled) return;
          setTestRepos(
            (res.data?.repositories ?? []).map(repo => ({
              value: repo.name,
              label: repo.name,
            })),
          );
        })
        .catch(err => {
          if (cancelled) return;
          setTestFetchError(
            getApiErrorMessage(err, 'Failed to fetch test repositories'),
          );
        })
        .finally(() => {
          if (!cancelled) setTestReposLoading(false);
        });
    } else {
      setTestProjectsLoading(true);
      setTestProjects([]);
      void getProjectsDetails({
        provider: 'azure-devops',
        body: { organization: selectedTestOrg },
        requesterToken: { patToken: token },
      })
        .then(res => {
          if (cancelled) return;
          setTestProjects(
            (res.data?.projects?.value ?? []).map(project => ({
              value: project.name,
              label: project.name,
            })),
          );
        })
        .catch(err => {
          if (cancelled) return;
          setTestFetchError(
            getApiErrorMessage(err, 'Failed to fetch test projects'),
          );
        })
        .finally(() => {
          if (!cancelled) setTestProjectsLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isGithub, selectedTestOrg, resetTestDownstream]);

  useEffect(() => {
    if (isGithub) return;
    setSelectedTestRepo('');
    setSelectedTestBranch('');
    setTestRepos([]);
    setTestBranches([]);
    setTestRepoRemoteUrls({});
    resetPipelinePlatformState();
    if (!selectedTestOrg || !selectedTestProject) return;

    const token = testPatRef.current.trim();
    if (!token) return;

    let cancelled = false;
    setTestReposLoading(true);
    void getRepositoryDetails({
      provider: 'azure-devops',
      body: { organization: selectedTestOrg, project: selectedTestProject },
      requesterToken: { patToken: token },
    })
      .then(res => {
        if (cancelled) return;
        const repos = res.data?.value ?? [];
        setTestRepos(
          repos.map(repo => ({
            value: repo.id,
            label: repo.name,
          })),
        );
        setTestRepoRemoteUrls(
          Object.fromEntries(
            repos.map(repo => [repo.id, repo.remoteUrl ?? '']),
          ),
        );
      })
      .catch(err => {
        if (cancelled) return;
        setTestFetchError(
          getApiErrorMessage(err, 'Failed to fetch test repositories'),
        );
      })
      .finally(() => {
        if (!cancelled) setTestReposLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isGithub, selectedTestOrg, selectedTestProject]);

  useEffect(() => {
    setSelectedTestBranch('');
    setTestBranches([]);
    resetPipelinePlatformState();
    if (!selectedTestRepo || !selectedTestOrg) return;
    if (!isGithub && !selectedTestProject) return;

    const token = testPatRef.current.trim();
    if (!token) return;

    let cancelled = false;
    setTestBranchesLoading(true);
    if (isGithub) {
      setTestWorkflowsLoading(true);
      setTestWorkflows([]);
      void Promise.all([
        getGithubBranches('github', token, selectedTestOrg, selectedTestRepo),
        getGithubWorkflows('github', token, selectedTestOrg, selectedTestRepo),
      ])
        .then(([branchRes, workflowRes]) => {
          if (cancelled) return;
          setTestBranches(
            (branchRes.data?.branches ?? []).map(branch => ({
              value: branch.name,
              label: branch.name,
            })),
          );
          setTestWorkflows(
            (workflowRes.data?.workflows ?? []).map(workflow => ({
              value: String(workflow.id),
              label: workflow.name,
            })),
          );
        })
        .catch(err => {
          if (cancelled) return;
          setTestFetchError(
            getApiErrorMessage(err, 'Failed to fetch test branches'),
          );
        })
        .finally(() => {
          if (!cancelled) {
            setTestBranchesLoading(false);
            setTestWorkflowsLoading(false);
          }
        });
    } else {
      setTestBranchesLoading(true);
      void getBranchesDetails(
        'azure-devops',
        {
          organization: selectedTestOrg,
          project: selectedTestProject,
          repositoryId: selectedTestRepo,
        },
        { patToken: token },
      )
        .then(res => {
          if (cancelled) return;
          setTestBranches(
            (res.data?.value ?? []).map(branch => ({
              value: branch.name,
              label: branch.name.replace(/^refs\/heads\//, ''),
            })),
          );
        })
        .catch(err => {
          if (cancelled) return;
          setTestFetchError(
            getApiErrorMessage(err, 'Failed to fetch test branches'),
          );
        })
        .finally(() => {
          if (!cancelled) setTestBranchesLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isGithub, selectedTestOrg, selectedTestProject, selectedTestRepo, resetPipelinePlatformState]);

  useEffect(() => {
    if (!selectedTestBranch) return;
    resetPipelinePlatformState();
  }, [selectedTestBranch, resetPipelinePlatformState]);

  useEffect(() => {
    if (!isGithub || !selectedTestWorkflow || !selectedTestRepo || !selectedTestOrg) {
      return;
    }

    const token = testPatRef.current.trim();
    if (!token) return;

    let cancelled = false;
    setSelectedTestJob('');
    setTestJobs([]);
    setTestJobsLoading(true);
    void getGithubWorkflowJobs(
      'github',
      token,
      selectedTestOrg,
      selectedTestRepo,
      selectedTestWorkflow,
      selectedTestBranch || undefined,
    )
      .then(res => {
        if (cancelled) return;
        setTestJobs(
          (res.data?.jobs ?? []).map(job => ({
            value: job.name,
            label: job.name,
          })),
        );
      })
      .catch(err => {
        if (cancelled) return;
        setTestFetchError(
          getApiErrorMessage(err, 'Failed to fetch test workflow jobs'),
        );
      })
      .finally(() => {
        if (!cancelled) setTestJobsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isGithub,
    selectedTestWorkflow,
    selectedTestOrg,
    selectedTestRepo,
    selectedTestBranch,
  ]);

  useEffect(() => {
    if (isGithub || testPipelinePlatform !== 'ado') return;
    if (!selectedTestOrg || !selectedTestProject || !selectedTestBranch) return;

    const token = testPatRef.current.trim();
    if (!token) return;

    let cancelled = false;
    setTestPipelinesLoading(true);
    setTestPipelines([]);
    setSelectedTestPipeline('');
    void getPipelineDetails({
      provider: 'azure-devops',
      body: { organization: selectedTestOrg, project: selectedTestProject },
      requesterToken: { patToken: token },
    })
      .then(pipelines => {
        if (cancelled) return;
        setTestPipelines(
          pipelines.map(pipeline => ({
            value: String(pipeline.id),
            label: pipeline.name,
          })),
        );
      })
      .catch(err => {
        if (cancelled) return;
        setTestFetchError(
          getApiErrorMessage(err, 'Failed to fetch test pipelines'),
        );
      })
      .finally(() => {
        if (!cancelled) setTestPipelinesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    isGithub,
    testPipelinePlatform,
    selectedTestOrg,
    selectedTestProject,
    selectedTestBranch,
  ]);

  const handleFetchJenkinsJobs = async () => {
    const baseUrl = jenkinsBaseUrl.trim();
    const username = jenkinsUsername.trim();
    const apiToken = jenkinsApiToken.trim();
    if (!baseUrl || !username || !apiToken) {
      setJenkinsJobsError('Jenkins URL, username, and API token are required.');
      return;
    }
    setJenkinsJobsLoading(true);
    setJenkinsJobsError(null);
    setJenkinsJobs([]);
    setJenkinsJobUrls({});
    setSelectedJenkinsJob('');
    try {
      const res = await fetchJenkinsJobs({ baseUrl, username, apiToken });
      const jobs = res.data?.jobs ?? [];
      setJenkinsJobs(
        jobs.map(job => ({
          value: job.fullName,
          label: job.fullName,
        })),
      );
      setJenkinsJobUrls(
        Object.fromEntries(jobs.map(job => [job.fullName, job.url ?? ''])),
      );
      if (jobs.length === 0) {
        setJenkinsJobsError('No Jenkins jobs found for these credentials.');
      }
    } catch (err) {
      setJenkinsJobsError(getApiErrorMessage(err, 'Failed to fetch Jenkins jobs'));
    } finally {
      setJenkinsJobsLoading(false);
    }
  };

  const handleLoadTestScripts = async () => {
    if (!selectedTestRepo || !selectedTestBranch) return;
    const token = testPat.trim();
    setScriptsLoading(true);
    setScriptsError(null);
    setScriptsLoaded(false);
    setScriptCandidates([]);
    setSelectedScriptPaths(new Set());
    try {
      if (isGithub) {
        const res = await getGithubTestScripts(
          'github',
          token,
          selectedTestOrg,
          selectedTestRepo,
          selectedTestBranch,
          { workflowId: selectedTestWorkflow || undefined },
        );
        if (!res.success) {
          setScriptsError(res.message || 'Failed to load test scripts');
          return;
        }
        const candidates = res.data?.candidates ?? [];
        setScriptCandidates(candidates);
        setSelectedScriptPaths(
          new Set(
            candidates
              .filter(candidate => candidate.selectedByDefault)
              .map(candidate => candidate.filePath),
          ),
        );
        setScriptsLoaded(true);
        return;
      }

      const releaseIds = pick.selectedReleases
        .map(id => parseInt(id, 10))
        .filter(id => !Number.isNaN(id));
      if (!releaseIds.length) {
        setScriptsError('Select at least one release pipeline.');
        return;
      }
      const testUrl = testRepoRemoteUrls[selectedTestRepo]?.trim() ?? '';
      if (!testUrl) {
        setScriptsError('Select a test repository and branch.');
        return;
      }

      const pipelineNames: Record<string, string> = {};
      for (const releaseId of pick.selectedReleases) {
        pipelineNames[releaseId] = pick.releasePipelineLabels[releaseId] ?? '';
      }

      const envRes = await getReleaseEnvironments(
        'azure-devops',
        {
          organization: pick.organization,
          project: pick.selectedProject,
          releaseDefinitionIds: releaseIds,
          pipelineNames,
        },
        pick.pat,
      );
      const selections = buildReleaseSelectionsFromEnvironments(
        envRes.definitions ?? [],
      );
      setReleaseSelections(selections);
      const candRes = await getScriptCandidates(
        {
          organization: pick.organization,
          project: pick.selectedProject,
          testOrgName: selectedTestOrg,
          testProjectName: selectedTestProject,
          testRepoUrl: testUrl,
          testBranchName: selectedTestBranch.trim(),
          testPat: token,
          testScriptsRootPath: '/',
          testScriptsRootPaths: ['/'],
          releaseConfig: {
            organization: pick.organization,
            project: pick.selectedProject,
            pat: pick.pat,
            pipelines: pick.selectedReleases.map(releaseId => ({
              releasePipelineId: parseInt(releaseId, 10),
              releasePipelineName: pick.releasePipelineLabels[releaseId] ?? '',
              lastReleaseId: null,
              lastReleaseProcessedAt: null,
            })),
          },
          releaseSelections: selections,
        },
        pick.pat,
      );
      if (!candRes.success) {
        setScriptsError(candRes.message || 'Failed to load test scripts');
        return;
      }
      const candidates = candRes.candidates ?? [];
      setScriptCandidates(candidates);
      setSelectedScriptPaths(new Set());
      setScriptsLoaded(true);
    } catch (err) {
      setScriptsError(getApiErrorMessage(err, 'Failed to load test scripts'));
    } finally {
      setScriptsLoading(false);
    }
  };

  const handleOnboard = async () => {
    if (!selectedTestRepo || !selectedTestBranch) {
      setOnboardError('Complete the test repository and branch fields.');
      return;
    }
    if (isGithub) {
      if (!testPipelinePlatform) {
        setOnboardError('Select a pipeline platform.');
        return;
      }
      if (
        testPipelinePlatform === 'github-actions' &&
        (!selectedTestWorkflow || !selectedTestJob)
      ) {
        setOnboardError('Select a test workflow and workflow job.');
        return;
      }
      if (testPipelinePlatform === 'jenkins') {
        if (
          !jenkinsBaseUrl.trim() ||
          !jenkinsUsername.trim() ||
          !jenkinsApiToken.trim() ||
          !selectedJenkinsJob
        ) {
          setOnboardError('Complete Jenkins fields and select a job.');
          return;
        }
      }
    } else if (!isGithub) {
      if (!testPipelinePlatform) {
        setOnboardError('Select a pipeline platform.');
        return;
      }
      if (testPipelinePlatform === 'ado' && !selectedTestPipeline) {
        setOnboardError('Select a test pipeline.');
        return;
      }
      if (testPipelinePlatform === 'jenkins') {
        if (
          !jenkinsBaseUrl.trim() ||
          !jenkinsUsername.trim() ||
          !jenkinsApiToken.trim() ||
          !selectedJenkinsJob
        ) {
          setOnboardError('Complete Jenkins fields and select a job.');
          return;
        }
      }
    }
    if (!scriptsLoaded || selectedScriptPaths.size === 0) {
      setOnboardError(
        scriptsLoaded
          ? 'Select at least one test script before onboarding.'
          : 'Load test scripts and select files before onboarding.',
      );
      return;
    }

    const token = testPat.trim();
    setOnboardLoading(true);
    setOnboardError(null);

    try {
      if (isGithub) {
        const testRepoUrl =
          `https://github.com/${selectedTestOrg}/${selectedTestRepo}`;
        const selectedTestWorkflowName =
          testWorkflows.find(workflow => workflow.value === selectedTestWorkflow)
            ?.label ?? '';
        const pipelinePlatform = (testPipelinePlatform ||
          'github-actions') as PipelinePlatform;
        const body: GithubDetectBody = {
          organization: pick.organization,
          repository: pick.selectedRepo,
          repoUrl: pick.repoRemoteUrl,
          branch: pick.selectedBranch,
          workflowId: pick.selectedWorkflow || undefined,
          workflowName: pick.selectedWorkflowName || undefined,
          workflowJob: pick.selectedJob || undefined,
          environment: pick.selectedEnvironments[0] || undefined,
          onboardedBy: ONBOARDED_BY,
          frontendUrl: pick.frontendUrl.trim() || undefined,
          backendUrl: pick.backendUrl.trim() || undefined,
          codeCredentials: { pat: pick.pat },
          testAutomation: {
            location: 'different',
            testOrganization: selectedTestOrg,
            testRepository: selectedTestRepo,
            testRepoUrl,
            testBranchName: selectedTestBranch,
            selectedScriptPaths: Array.from(selectedScriptPaths),
            testPat: token,
            pipelinePlatform,
            ...(pipelinePlatform === 'github-actions'
              ? {
                  testWorkflowId: selectedTestWorkflow || undefined,
                  testWorkflowName: selectedTestWorkflowName || undefined,
                  testWorkflowJob: selectedTestJob || undefined,
                }
              : {}),
            ...(pipelinePlatform === 'jenkins'
              ? {
                  jenkins: {
                    baseUrl: jenkinsBaseUrl.trim(),
                    username: jenkinsUsername.trim(),
                    apiToken: jenkinsApiToken.trim(),
                    jobName: selectedJenkinsJob.trim(),
                    jobUrl: jenkinsJobUrls[selectedJenkinsJob],
                  },
                }
              : {}),
          },
        };

        const res = await detectGithubRepository('github', body, pick.pat);
        if (!res.success) {
          throw new Error(res.message || 'Onboarding failed');
        }

        const files = res.repoFiles?.repoResponse?.files ?? [];
        const analysis = filterNonTestFiles(files);
        const repoId = res.onboardData?.repositoryId ?? '';
        const branchId = res.onboardData?.branchId ?? '';
        if (!repoId || !branchId) {
          throw new Error(
            'Onboarding did not return repository IDs. Please try again.',
          );
        }

        if (pick.captureBeforeAfter && repoId && pick.frontendUrl.trim()) {
          try {
            await enableRepositoryCapture(repoId, {
              frontendUrl: pick.frontendUrl.trim(),
              enabled: true,
              optedInBy: ONBOARDED_BY,
              branch: pick.selectedBranch,
              repositoryName: pick.selectedRepo,
            });
          } catch {
            // Non-blocking — same as GitHub onboarding view.
          }
        }

        dispatch(
          setOnboardingEmails({
            prEmails: pick.scanOnly ? [] : pick.applicationPrEmails,
            autoPrEmails: pick.scanOnly ? [] : pick.automationPrEmails,
          }),
        );
        dispatch(setScanOnly(pick.scanOnly));
        dispatch(
          setOnboardingBatchInputs({
            scmProvider: 'github',
            files: [...analysis.nonTestFiles, ...analysis.nonTestlargerFiles],
            patToken: pick.pat,
            repoUrl: pick.repoRemoteUrl,
            branchName: pick.selectedBranch,
            pipelineName: pick.selectedWorkflow || pick.selectedWorkflowName,
            azureProject: '',
            repoId,
            repoName: pick.selectedRepo,
            branchId,
            org: pick.organization,
            testPatToken: token,
            testOrg: selectedTestOrg,
            testProject: '',
            testPipelineName:
              pipelinePlatform === 'jenkins'
                ? selectedJenkinsJob
                : selectedTestWorkflowName || selectedTestWorkflow,
            testRepoUrl,
            testBranchName: selectedTestBranch,
            testPipelinePlatform: pipelinePlatform,
            jenkinsBaseUrl: jenkinsBaseUrl.trim(),
            jenkinsUsername: jenkinsUsername.trim(),
            jenkinsJobName: selectedJenkinsJob,
            isFrontend: analysis.isFrontend,
          }),
        );
      } else {
        if (!pick.selectedRepoDetectData || !pick.selectedProjectId) {
          throw new Error(
            'Repository metadata is missing. Go back and re-select the repository.',
          );
        }
        const testUrl = testRepoRemoteUrls[selectedTestRepo]?.trim() ?? '';
        const selectedTestPipelineName =
          testPipelines.find(pipeline => pipeline.value === selectedTestPipeline)
            ?.label ?? '';
        const pipelinePlatform = (testPipelinePlatform || 'ado') as PipelinePlatform;
        const body: DetectRepositoryBody = {
          organization: {
            accountId: pick.organizationAccountId ?? '',
            accountName: pick.organization,
            accountUri: pick.organizationAccountUri ?? '',
          },
          allProjects: [
            {
              id: pick.selectedProjectId,
              name: pick.selectedProject,
              repositories: [pick.selectedRepoDetectData],
            },
          ],
          selectedProjectId: pick.selectedProject,
          selectedRepositoryId: pick.selectedRepo,
          selectedBranch: { name: pick.selectedBranch, isDefault: false },
          repoUrl: pick.repoRemoteUrl,
          onboardedBy: ONBOARDED_BY,
          pipelineId: pick.selectedPipeline,
          pipelineName: pick.selectedPipelineName,
          commitId: pick.selectedBranchObjectId ?? '',
          pipelineStageNames: pick.selectedStage ? [pick.selectedStage] : [],
          testAutomation: {
            location: 'different',
            testOrgName: selectedTestOrg,
            testProjectName: selectedTestProject,
            testAdoRepoId: selectedTestRepo,
            testRepoUrl: testUrl,
            testBranchName: selectedTestBranch,
            testScriptsRootPath: '/',
            testScriptsRootPaths: ['/'],
            selectedScriptPaths: Array.from(selectedScriptPaths),
            releaseSelections,
            testPipelineId:
              pipelinePlatform === 'jenkins'
                ? selectedJenkinsJob
                : selectedTestPipeline,
            testPipelineName:
              pipelinePlatform === 'jenkins'
                ? selectedJenkinsJob
                : selectedTestPipelineName,
            testPat: token,
            pipelinePlatform,
            ...(pipelinePlatform === 'jenkins'
              ? {
                  jenkins: {
                    baseUrl: jenkinsBaseUrl.trim(),
                    username: jenkinsUsername.trim(),
                    apiToken: jenkinsApiToken.trim(),
                    jobName: selectedJenkinsJob.trim(),
                    jobUrl: jenkinsJobUrls[selectedJenkinsJob],
                  },
                }
              : {}),
          },
          frontendUrl: pick.frontendUrl.trim(),
          backendUrl: pick.backendUrl.trim(),
          releaseConfig: {
            organization: pick.organization,
            project: pick.selectedProject,
            pat: pick.pat,
            pipelines: pick.selectedReleases.map(releaseId => ({
              releasePipelineId: parseInt(releaseId, 10),
              releasePipelineName: pick.releasePipelineLabels[releaseId] ?? '',
              lastReleaseId: null,
              lastReleaseProcessedAt: null,
            })),
          },
        };

        const res = await detectRepository(
          assertScmProvider(pick.provider),
          body,
          pick.pat,
        );
        if (res.success === false) {
          throw new Error(res.message || 'Onboarding failed');
        }
        const files = res?.repoFiles?.data?.repoResponse?.files ?? [];
        const analysis = filterNonTestFiles(files);
        const repoId = res?.onboardData?.activeRepositoryId ?? '';
        const branchId = res?.onboardData?.activeBranchId ?? '';
        if (!repoId || !branchId) {
          throw new Error(
            'Onboarding did not return repository IDs. Please try again.',
          );
        }

        if (pick.captureBeforeAfter && repoId && pick.frontendUrl.trim()) {
          try {
            await enableRepositoryCapture(repoId, {
              frontendUrl: pick.frontendUrl.trim(),
              enabled: true,
              optedInBy: ONBOARDED_BY,
              branch: pick.selectedBranch,
              repositoryName: pick.selectedRepoName,
            });
          } catch {
            // Non-blocking — same as GitHub onboarding view.
          }
        }

        dispatch(
          setOnboardingEmails({
            prEmails: pick.scanOnly ? [] : pick.applicationPrEmails,
            autoPrEmails: pick.scanOnly ? [] : pick.automationPrEmails,
          }),
        );
        dispatch(setScanOnly(pick.scanOnly));
        dispatch(
          setOnboardingBatchInputs({
            scmProvider: assertScmProvider(pick.provider),
            files: [...analysis.nonTestFiles, ...analysis.nonTestlargerFiles],
            patToken: pick.pat,
            repoUrl: pick.repoRemoteUrl,
            branchName: pick.selectedBranch,
            pipelineName: pick.selectedPipelineName,
            azureProject: pick.selectedProject,
            repoId,
            repoName: pick.selectedRepoName,
            branchId,
            org: pick.organization,
            testPatToken: token,
            testOrg: selectedTestOrg,
            testProject: selectedTestProject,
            testPipelineName:
              pipelinePlatform === 'jenkins'
                ? selectedJenkinsJob
                : selectedTestPipelineName,
            testRepoUrl: testUrl,
            testBranchName: selectedTestBranch,
            testPipelinePlatform: pipelinePlatform,
            jenkinsBaseUrl: jenkinsBaseUrl.trim(),
            jenkinsUsername: jenkinsUsername.trim(),
            jenkinsJobName: selectedJenkinsJob,
            isFrontend: analysis.isFrontend,
          }),
        );
      }

      onOnboardComplete();
    } catch (err) {
      setOnboardError(getApiErrorMessage(err, 'Onboarding failed'));
    } finally {
      setOnboardLoading(false);
    }
  };

  const canLoadTestScripts = isGithub
    ? Boolean(
        selectedTestBranch &&
          testPipelinePlatform &&
          (testPipelinePlatform === 'github-actions'
            ? selectedTestWorkflow && selectedTestJob
            : selectedJenkinsJob),
      )
    : Boolean(
        selectedTestBranch &&
          testPipelinePlatform &&
          (testPipelinePlatform === 'ado'
            ? selectedTestPipeline
            : selectedJenkinsJob),
      );

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--inv-bg)]">
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-8 pt-8 max-[520px]:px-5">
        <AddRepositoryBackButton onBack={onBack} />
        <OnboardingPageHeader
          eyebrow="Add Repository"
          title="Test Automation"
        />

        <div className="mt-2">
          <label
            htmlFor="add-repository-test-pat"
            className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
            Personal Access Token (Test Automation)
          </label>
          <PasswordInput
            id="add-repository-test-pat"
            title="Personal Access Token (Test Automation)"
            placeholder="Enter your test PAT"
            value={testPat}
            onChange={value => {
              setTestPat(value);
              setTestFetchError(null);
            }}
            className="font-mono"
          />
          {testOrgsLoading ? (
            <div className="mt-3 flex items-center gap-2 text-[0.8125rem] text-[var(--inv-text-muted)]">
              <Spinner compact />
              Fetching Organizations
            </div>
          ) : null}
          {testFetchError ? (
            <p className="mt-2 mb-0 text-[0.8125rem] text-[#b42318]" role="alert">
              {testFetchError}
            </p>
          ) : null}
        </div>

        {!testOrgsLoading && testOrgs.length > 0 ? (
          <FilterSelect
            id="add-repository-test-org"
            label="Test Organization"
            items={testOrgs}
            value={selectedTestOrg}
            onSelect={setSelectedTestOrg}
            disabled={testOrgs.length === 0}
          />
        ) : null}

        {isGithub ? null : (
          <>
            <FilterSelect
              id="add-repository-test-project"
              label="Test Project"
              items={testProjects}
              value={selectedTestProject}
              onSelect={setSelectedTestProject}
              disabled={!selectedTestOrg || testProjectsLoading}
              loading={testProjectsLoading}
            />
            <FilterSelect
              id="add-repository-test-repo"
              label="Test Repository"
              items={testRepos}
              value={selectedTestRepo}
              onSelect={setSelectedTestRepo}
              disabled={!selectedTestProject || testReposLoading}
              loading={testReposLoading}
            />

            <FilterSelect
              id="add-repository-test-branch"
              label="Test Branch"
              items={testBranches}
              value={selectedTestBranch}
              onSelect={setSelectedTestBranch}
              disabled={!selectedTestRepo || testBranchesLoading}
              loading={testBranchesLoading}
            />

            {selectedTestBranch ? (
              <>
                <FilterSelect
                  id="add-repository-test-pipeline-platform"
                  label="Pipeline Platform"
                  items={ADO_PIPELINE_PLATFORM_OPTIONS}
                  value={testPipelinePlatform}
                  onSelect={value => {
                    setTestPipelinePlatform(value as PipelinePlatform);
                    setTestPipelines([]);
                    setSelectedTestPipeline('');
                    setJenkinsJobs([]);
                    setSelectedJenkinsJob('');
                    setJenkinsJobsError(null);
                    resetScriptState();
                  }}
                  disabled={!selectedTestBranch}
                />

                {testPipelinePlatform === 'ado' ? (
                  <FilterSelect
                    id="add-repository-test-pipeline"
                    label="Test Pipeline"
                    items={testPipelines}
                    value={selectedTestPipeline}
                    onSelect={value => {
                      setSelectedTestPipeline(value);
                      resetScriptState();
                    }}
                    disabled={!selectedTestBranch || testPipelinesLoading}
                    loading={testPipelinesLoading}
                  />
                ) : null}

                {testPipelinePlatform === 'jenkins' ? (
                  <>
                    <div className="mt-6 grid grid-cols-1 gap-4 min-[520px]:grid-cols-3">
                      <div>
                        <label
                          htmlFor="add-repository-jenkins-url"
                          className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
                          Jenkins URL
                        </label>
                        <Input
                          id="add-repository-jenkins-url"
                          value={jenkinsBaseUrl}
                          onChange={event => setJenkinsBaseUrl(event.target.value)}
                          placeholder="e.g. http://13.205.65.111:8080"
                          className="font-mono"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="add-repository-jenkins-username"
                          className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
                          Jenkins Username
                        </label>
                        <Input
                          id="add-repository-jenkins-username"
                          value={jenkinsUsername}
                          onChange={event =>
                            setJenkinsUsername(event.target.value)
                          }
                          placeholder="Jenkins login username"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="add-repository-jenkins-api-token"
                          className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
                          Jenkins API Token
                        </label>
                        <PasswordInput
                          id="add-repository-jenkins-api-token"
                          title="Jenkins API Token"
                          placeholder="API token for this user"
                          value={jenkinsApiToken}
                          onChange={setJenkinsApiToken}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-10"
                        disabled={
                          jenkinsJobsLoading ||
                          !jenkinsBaseUrl.trim() ||
                          !jenkinsUsername.trim() ||
                          !jenkinsApiToken.trim()
                        }
                        onClick={() => void handleFetchJenkinsJobs()}>
                        {jenkinsJobsLoading
                          ? 'Fetching jobs…'
                          : 'Fetch Jenkins Jobs'}
                      </Button>
                    </div>
                    {jenkinsJobsError ? (
                      <p
                        className="mt-2 mb-0 text-[0.8125rem] text-[#b42318]"
                        role="alert">
                        {jenkinsJobsError}
                      </p>
                    ) : null}
                    <FilterSelect
                      id="add-repository-jenkins-job"
                      label="Jenkins Job"
                      items={jenkinsJobs}
                      value={selectedJenkinsJob}
                      onSelect={value => {
                        setSelectedJenkinsJob(value);
                        resetScriptState();
                      }}
                      disabled={jenkinsJobs.length === 0 || jenkinsJobsLoading}
                      loading={jenkinsJobsLoading}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </>
        )}

        {isGithub ? (
          <>
            <FilterSelect
              id="add-repository-test-repo"
              label="Test Repository"
              items={testRepos}
              value={selectedTestRepo}
              onSelect={setSelectedTestRepo}
              disabled={!selectedTestOrg || testReposLoading}
              loading={testReposLoading}
            />

            <FilterSelect
              id="add-repository-test-branch"
              label="Test Branch"
              items={testBranches}
              value={selectedTestBranch}
              onSelect={setSelectedTestBranch}
              disabled={!selectedTestRepo || testBranchesLoading}
              loading={testBranchesLoading}
            />

            {selectedTestBranch ? (
              <>
                <FilterSelect
                  id="add-repository-test-pipeline-platform-github"
                  label="Pipeline Platform"
                  items={GITHUB_PIPELINE_PLATFORM_OPTIONS}
                  value={testPipelinePlatform}
                  onSelect={value => {
                    setTestPipelinePlatform(value as PipelinePlatform);
                    setSelectedTestWorkflow('');
                    setTestJobs([]);
                    setSelectedTestJob('');
                    setJenkinsJobs([]);
                    setSelectedJenkinsJob('');
                    setJenkinsJobsError(null);
                    resetScriptState();
                  }}
                  disabled={!selectedTestBranch}
                />

                {testPipelinePlatform === 'github-actions' ? (
                  <>
                    <FilterSelect
                      id="add-repository-test-workflow"
                      label="Test Workflow"
                      items={testWorkflows}
                      value={selectedTestWorkflow}
                      onSelect={value => {
                        setSelectedTestWorkflow(value);
                        resetScriptState();
                      }}
                      disabled={!selectedTestRepo || testWorkflowsLoading}
                      loading={testWorkflowsLoading}
                    />
                    <FilterSelect
                      id="add-repository-test-workflow-job"
                      label="Test Workflow Job"
                      items={testJobs}
                      value={selectedTestJob}
                      onSelect={value => {
                        setSelectedTestJob(value);
                        resetScriptState();
                      }}
                      disabled={!selectedTestWorkflow || testJobsLoading}
                      loading={testJobsLoading}
                    />
                  </>
                ) : null}

                {testPipelinePlatform === 'jenkins' ? (
                  <>
                    <div className="mt-6 grid grid-cols-1 gap-4 min-[520px]:grid-cols-3">
                      <div>
                        <label
                          htmlFor="add-repository-github-jenkins-url"
                          className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
                          Jenkins URL
                        </label>
                        <Input
                          id="add-repository-github-jenkins-url"
                          value={jenkinsBaseUrl}
                          onChange={event => setJenkinsBaseUrl(event.target.value)}
                          placeholder="e.g. http://13.205.65.111:8080"
                          className="font-mono"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="add-repository-github-jenkins-username"
                          className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
                          Jenkins Username
                        </label>
                        <Input
                          id="add-repository-github-jenkins-username"
                          value={jenkinsUsername}
                          onChange={event =>
                            setJenkinsUsername(event.target.value)
                          }
                          placeholder="Jenkins login username"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="add-repository-github-jenkins-api-token"
                          className="mb-2 block text-[0.9375rem] font-semibold text-[var(--inv-text)]">
                          Jenkins API Token
                        </label>
                        <PasswordInput
                          id="add-repository-github-jenkins-api-token"
                          title="Jenkins API Token"
                          placeholder="API token for this user"
                          value={jenkinsApiToken}
                          onChange={setJenkinsApiToken}
                          className="font-mono"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-10"
                        disabled={
                          jenkinsJobsLoading ||
                          !jenkinsBaseUrl.trim() ||
                          !jenkinsUsername.trim() ||
                          !jenkinsApiToken.trim()
                        }
                        onClick={() => void handleFetchJenkinsJobs()}>
                        {jenkinsJobsLoading
                          ? 'Fetching jobs…'
                          : 'Fetch Jenkins Jobs'}
                      </Button>
                    </div>
                    {jenkinsJobsError ? (
                      <p
                        className="mt-2 mb-0 text-[0.8125rem] text-[#b42318]"
                        role="alert">
                        {jenkinsJobsError}
                      </p>
                    ) : null}
                    <FilterSelect
                      id="add-repository-github-jenkins-job"
                      label="Jenkins Job"
                      items={jenkinsJobs}
                      value={selectedJenkinsJob}
                      onSelect={value => {
                        setSelectedJenkinsJob(value);
                        resetScriptState();
                      }}
                      disabled={jenkinsJobs.length === 0 || jenkinsJobsLoading}
                      loading={jenkinsJobsLoading}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </>
        ) : null}

        <div className="mt-6">
          <Button
            type="button"
            variant="secondary"
            className="h-10 w-full"
            disabled={!canLoadTestScripts || scriptsLoading}
            onClick={() => void handleLoadTestScripts()}>
            {scriptsLoading ? 'Loading test scripts…' : 'Load test scripts'}
          </Button>
        </div>
        {!scriptsLoaded && !scriptsLoading ? (
          <p className="mt-2 mb-0 text-[0.8125rem] font-normal leading-[1.5] text-[var(--inv-text-muted)]">
            Load scripts from the test repo using release pipeline Katalon
            hints, then select files to index.
          </p>
        ) : null}
        {scriptsLoaded ? (
          <TestScriptSelectionCard
            candidates={scriptCandidates}
            selectedPaths={selectedScriptPaths}
            onSelectionChange={setSelectedScriptPaths}
            loading={false}
            error={scriptsError}
          />
        ) : null}
      </div>

      <div className="shrink-0 border-t border-solid border-[var(--inv-border)] bg-[var(--inv-bg)] px-8 py-4 max-[520px]:px-5">
        {onboardError ? (
          <p className="mb-3 mt-0 text-[0.8125rem] text-[#b42318]" role="alert">
            {onboardError}
          </p>
        ) : null}
        <div className="flex items-center justify-end">
          <Button
            type="button"
            className="h-10 whitespace-nowrap"
            disabled={onboardLoading}
            onClick={() => void handleOnboard()}>
            {onboardLoading ? 'Onboarding…' : 'Onboard'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddRepositoryTestAutomationPanel;
